import { CloseOutlined, MoreOutlined, PlusOutlined, SearchOutlined, ShareAltOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Flex, Input, MenuProps, Segmented, Tooltip, Typography, message } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { deleteClient, getAllClients } from "../../service/client.service";
import {
  setCreateClientState,
  setCustomiseSectionState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
  setShareClientModalClient,
  setShareClientModalVisible,
  setShowDemoSignupModal,
} from "../../slices/appStateSlice";
import { UserTier } from "../../slices/authSlice";
import {
  resetClientForm,
  setClientForm,
  setClients,
  setFilteredOwnedClients,
  setFilteredSharedClients,
  setIsEditing,
  setSharedClients,
} from "../../slices/clientSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import type { TClientForm } from "../../types/index.type";
import { sortClientsByName } from "../../utils/datamanipulation.utils";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import EmptyStateClient from "../EmptyState/EmptyStateClient.component";
import styles from "./clientview.module.scss";

const ClientView = ({
  demoClients,
  addClientRef,
  clientViewRef,
}: {
  demoClients?: TClientForm[];
  addClientRef?: React.RefObject<HTMLDivElement>;
  clientViewRef?: React.RefObject<HTMLDivElement>;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { referIndividualBy } = useSpecialityReference();
  const componentName = "ClientView";

  const [searchTerm, setSearchTerm] = React.useState("");
  const [debounceTimeout, setDebounceTimeout] = React.useState<NodeJS.Timeout>();
  const [selectedSegment, setSelectedSegment] = React.useState<"all" | "shared">("all");

  const { rootPath } = useRootPath();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { selectedClient, isDemoPath, isMobilePath } = useAppSelector((state) => state.appState);

  const { isRecording, isPaused } = useAppSelector((state) => state.recordings);

  const { clients, sharedClients, filteredOwnedClients, filteredSharedClients } = useAppSelector(
    (state) => state.client,
  );

  const selectClient = (client: TClientForm | null, showLoader = true) => {
    if (selectedClient?.id && client && client.id && selectedClient.id === client.id) {
      return;
    }
    if (showLoader) {
      dispatch(setLoadingNotesState(true));
    }
    dispatch(setSelectedClient(client));
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  const handleClientSelectWithNavigate = (client: TClientForm) => {
    selectClient(client);
    navigate(`${rootPath}/clients/${client.uuid}`);
  };

  const clearSelection = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    navigate(rootPath);
    selectClient(null);
  };

  const showClientView = () => {
    dispatch(setCreateClientState(true));
    dispatch(setIsEditing(false));
    dispatch(resetClientForm());
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  // biome-ignore lint: no need to add selectedClient to the dependency array
  React.useEffect(() => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (searchTerm === "" && !demoClients) {
      dispatch(setFilteredOwnedClients(clients));
      dispatch(setFilteredSharedClients(sharedClients));
      return;
    }

    // Set a new timeout to debounce the filtering function
    const timeout = setTimeout(() => {
      const filterClients = (clientList: TClientForm[]) => {
        return clientList.filter((item) => {
          const lowerSearchTerm = searchTerm.toLowerCase();
          const formattedDob = item.date_of_birth ? dayjs(item.date_of_birth).format("MM/DD/YYYY") : "";

          return (
            item.name.toLowerCase().includes(lowerSearchTerm) ||
            (item.mrn && item.mrn.toLowerCase().includes(lowerSearchTerm)) ||
            (formattedDob && formattedDob.includes(lowerSearchTerm))
          );
        });
      };

      // Only filter the list based on the selected segment
      if (selectedSegment === "all") {
        dispatch(setFilteredOwnedClients(filterClients(clients)));
        dispatch(setFilteredSharedClients(sharedClients));
      } else {
        dispatch(setFilteredOwnedClients(clients));
        dispatch(setFilteredSharedClients(filterClients(sharedClients)));
      }
    }, 300); // Adjust the delay (in milliseconds) as needed

    // Save the timeout id to be able to clear it later
    setDebounceTimeout(timeout);
  }, [clients, sharedClients, searchTerm, selectedSegment]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const fetchClients = () => {
    getAllClients()
      .then((response) => {
        const ownedClients = sortClientsByName(response.data.owned || []);
        const shared = sortClientsByName(response.data.shared || []);
        dispatch(setClients([...ownedClients, ...shared]));
        dispatch(setSharedClients(shared));
        dispatch(setFilteredOwnedClients(ownedClients));
        dispatch(setFilteredSharedClients(shared));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    if (!demoClients) {
      fetchClients();
    }
  }, []);

  const handleMenu = (e: any) => {
    e.domEvent.stopPropagation();
    if (e.key === "1") {
      dispatch(setCreateClientState(true));
      if (selectedClient) dispatch(setClientForm(selectedClient));
      dispatch(setIsEditing(true));
    }
    if (e.key === "2") {
      // Share action
      if (selectedClient) {
        dispatch(setShareClientModalClient(selectedClient));
        dispatch(setShareClientModalVisible(true));
      }
    }
    if (e.key === "3") {
      if (isDemoPath) {
        dispatch(setShowDemoSignupModal(true));
        return;
      }
      Swal.fire({
        title: `Would you like to delete this ${referIndividualBy.toLowerCase()}?`,
        text: `The ${referIndividualBy.toLowerCase()}'s notes will still be available unless you delete them.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, keep it",
      }).then((result) => {
        if (result.isConfirmed && selectedClient && selectedClient.id) {
          deleteClient(selectedClient.id)
            .then(() => {
              navigate(rootPath);
              fetchClients();
              dispatch(setSelectedClient(null));
              message.success(`${referIndividualBy} deleted successfully`);
            })
            .catch((error) => {
              console.error(error);
              message.error(`Failed to delete ${referIndividualBy.toLowerCase()}.`);
            });
        }
      });
    }
  };

  // Menu items - show edit for editable clients, share/delete only for owned clients
  const isOwnedClient = selectedClient?.is_owned !== false; // undefined or true means owned
  const canEditClient = selectedClient?.can_edit !== false; // undefined or true means can edit

  const items: MenuProps["items"] = (() => {
    const menuItems: MenuProps["items"] = [];
    if (canEditClient && !isMobilePath) {
      menuItems.push({
        key: "1",
        label: <span>Edit {referIndividualBy}</span>,
      });
    }
    if (isOwnedClient && !isMobilePath) {
      menuItems.push({
        key: "2",
        label: <span>Share {referIndividualBy}</span>,
      });
    }
    if (isOwnedClient) {
      menuItems.push({
        key: "3",
        label: <span>Delete {referIndividualBy}</span>,
      });
    }
    return menuItems;
  })();

  const allClients = [...filteredOwnedClients];

  return (
    <Flex
      vertical
      gap={10}
      className={
        !isRecording && !isPaused
          ? styles[`${componentName}__container`]
          : isRecording
            ? styles[`${componentName}__container-active-recording`]
            : isPaused
              ? styles[`${componentName}__container-paused-recording`]
              : ""
      }
      ref={clientViewRef}
    >
      <Flex justify="space-between" align="center" className={styles[`${componentName}__container-header`]}>
        {/* <Typography className={styles[`${componentName}__container-title`]}>{referIndividualBy}s</Typography> */}
        <Segmented
          className="client-segmented"
          options={[
            {
              label: `All ${referIndividualBy}s`,
              value: "all",
            },
            {
              label: "Shared",
              value: "shared",
            },
          ]}
          value={selectedSegment}
          onChange={(value) => {
            setSelectedSegment(value as "all" | "shared");
            setSearchTerm(""); // Clear search when switching segments
          }}
          block
        />
        <Button
          className="secondary-button"
          type="primary"
          size="middle"
          icon={<PlusOutlined />}
          onClick={showClientView}
          style={{ padding: "0 10px" }}
        >
          Add
        </Button>
      </Flex>
      {(clients.length > 0 || sharedClients.length > 0) && (
        <>
          <Flex>
            <Input
              value={searchTerm}
              prefix={<SearchOutlined />}
              placeholder={`Search ${referIndividualBy}s`}
              onChange={handleInputChange}
              allowClear
              style={{ padding: "5px 10px" }}
            />
          </Flex>
        </>
      )}
      {
        <Flex
          vertical
          className={
            styles[
              `${componentName}__container-scroll${
                (selectedSegment === "all" && filteredOwnedClients.length === 0) ||
                (selectedSegment === "shared" && filteredSharedClients.length === 0)
                  ? "-empty"
                  : ""
              }`
            ]
          }
        >
          {(selectedSegment === "all" && allClients.length > 0) ||
          (selectedSegment === "shared" && filteredSharedClients.length > 0) ? (
            <>
              {/* My Clients Section */}
              {selectedSegment === "all" && allClients.length > 0 && (
                <>
                  {allClients.map((data, index) => {
                    // Calculate inline details for MRN and DOB
                    const formattedDob = data.date_of_birth ? dayjs(data.date_of_birth).format("MM/DD/YYYY") : "";
                    const derivedAgeFromDob =
                      data.date_of_birth && formattedDob ? dayjs().diff(dayjs(data.date_of_birth), "year") : null;
                    const rawAge =
                      typeof data.age === "number"
                        ? data.age
                        : data.age
                          ? Number.parseInt(data.age.toString(), 10)
                          : null;
                    const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

                    const inlineDetails: string[] = [];
                    if (formattedDob) {
                      inlineDetails.push(formattedDob);
                    } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
                      inlineDetails.push(`Age ${displayAge}`);
                    }
                    if (data.mrn) {
                      inlineDetails.push(`MRN: ${data.mrn}`);
                    }

                    return (
                      <Flex
                        key={index}
                        gap={10}
                        align="center"
                        justify="space-between"
                        className={`${styles[`${componentName}__container-client`]} ${
                          selectedClient && selectedClient.id === data.id
                            ? styles[`${componentName}__container-client-selected`]
                            : ""
                        }`}
                        onClick={() => handleClientSelectWithNavigate(data)}
                      >
                        <Flex align="center" gap={10} style={{ flex: 1, minWidth: 0 }}>
                          <Avatar
                            size={"small"}
                            className="special-avatar"
                            style={{
                              backgroundColor: getRandomAvatarColor(data.name),
                              minWidth: "24px",
                            }}
                          >
                            {data.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                            <Flex align="center" gap={5} style={{ minWidth: 0 }}>
                              <Typography className={`${styles[`${componentName}__container-client-name`]}`}>
                                {data.name}
                              </Typography>
                              {!data.is_owned && data.owner_name && (
                                <Tooltip placement="top" title={`Shared by ${data.owner_name}`}>
                                  <svg
                                    style={{ flexShrink: 0 }}
                                    width="14"
                                    height="11"
                                    viewBox="0 0 14 11"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M7.41792 2.5C7.41792 3.16304 7.15453 3.79893 6.68569 4.26777C6.21685 4.73661 5.58096 5 4.91792 5C4.25488 5 3.61899 4.73661 3.15015 4.26777C2.68131 3.79893 2.41792 3.16304 2.41792 2.5C2.41792 1.83696 2.68131 1.20107 3.15015 0.732233C3.61899 0.263392 4.25488 0 4.91792 0C5.58096 0 6.21685 0.263392 6.68569 0.732233C7.15453 1.20107 7.41792 1.83696 7.41792 2.5ZM9.81792 10.006C9.92792 10.548 9.46992 11 8.91792 11H0.917919C0.364919 11 -0.0920809 10.548 0.0159191 10.006C0.245969 8.87575 0.859558 7.85974 1.75279 7.13C2.64602 6.40027 3.764 6.00165 4.91742 6.00165C6.07084 6.00165 7.18882 6.40027 8.08205 7.13C8.97528 7.85974 9.58887 8.87575 9.81892 10.006H9.81792ZM12.9199 10H11.3299C11.3221 9.90265 11.3088 9.80582 11.2899 9.71C11.0992 8.76638 10.7006 7.87714 10.1229 7.107C10.8579 6.9047 11.6424 6.98961 12.3171 7.34451C12.9918 7.69941 13.5062 8.29771 13.7559 9.018C13.9359 9.54 13.4729 10 12.9199 10ZM10.9179 6C11.4484 6 11.9571 5.78929 12.3321 5.41421C12.7072 5.03914 12.9179 4.53043 12.9179 4C12.9179 3.46957 12.7072 2.96086 12.3321 2.58579C11.9571 2.21071 11.4484 2 10.9179 2C10.3875 2 9.87878 2.21071 9.50371 2.58579C9.12863 2.96086 8.91792 3.46957 8.91792 4C8.91792 4.53043 9.12863 5.03914 9.50371 5.41421C9.87878 5.78929 10.3875 6 10.9179 6Z"
                                      fill="#9F9F9F"
                                    />
                                  </svg>
                                </Tooltip>
                              )}
                            </Flex>
                            {selectedClient && selectedClient.id === data.id && inlineDetails.length > 0 && (
                              <Typography className={`${styles[`${componentName}__container-client-details`]}`}>
                                {inlineDetails.join(" · ")}
                              </Typography>
                            )}
                          </Flex>
                        </Flex>
                        <Flex gap={10}>
                          <ShareAltOutlined
                            style={{ color: selectedClient ? "var(--indigo-700)" : "var(--grey-text)" }}
                            onClick={(e) => {
                              if (selectedClient && data.id === selectedClient.id) {
                                // if user is pro_tier continue else show toast it is only available for group users
                                if (userInfo?.tier === UserTier.GROUP) {
                                  e.stopPropagation();
                                  dispatch(setShareClientModalClient(data));
                                  dispatch(setShareClientModalVisible(true));
                                } else {
                                  message.error("This feature is only available for group users");
                                }
                              }
                            }}
                          />
                          {selectedClient && selectedClient.id === data.id && (
                            <>
                              <CloseOutlined style={{ color: "var(--indigo-700)" }} onClick={clearSelection} />
                              <Dropdown
                                menu={{ items, onClick: handleMenu }}
                                placement="bottomLeft"
                                arrow
                                trigger={["click"]}
                              >
                                <MoreOutlined style={{ color: "var(--indigo-700)" }} />
                              </Dropdown>
                            </>
                          )}
                        </Flex>
                      </Flex>
                    );
                  })}
                </>
              )}

              {/* Shared with Me Section */}
              {selectedSegment === "shared" && filteredSharedClients.length > 0 && (
                <>
                  {filteredSharedClients.map((data, index) => {
                    // Calculate inline details for MRN and DOB
                    const formattedDob = data.date_of_birth ? dayjs(data.date_of_birth).format("MM/DD/YYYY") : "";
                    const derivedAgeFromDob =
                      data.date_of_birth && formattedDob ? dayjs().diff(dayjs(data.date_of_birth), "year") : null;
                    const rawAge =
                      typeof data.age === "number"
                        ? data.age
                        : data.age
                          ? Number.parseInt(data.age.toString(), 10)
                          : null;
                    const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

                    const inlineDetails: string[] = [];
                    if (formattedDob) {
                      inlineDetails.push(formattedDob);
                    } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
                      inlineDetails.push(`Age ${displayAge}`);
                    }
                    if (data.mrn) {
                      inlineDetails.push(`MRN: ${data.mrn}`);
                    }

                    return (
                      <Flex
                        key={index}
                        gap={10}
                        align="center"
                        justify="space-between"
                        className={`${styles[`${componentName}__container-client`]} ${
                          selectedClient && selectedClient.id === data.id
                            ? styles[`${componentName}__container-client-selected`]
                            : ""
                        }`}
                        onClick={() => handleClientSelectWithNavigate(data)}
                      >
                        <Flex align="center" gap={10} style={{ flex: 1, minWidth: 0 }}>
                          <Avatar
                            size={"small"}
                            className="special-avatar"
                            style={{
                              backgroundColor: getRandomAvatarColor(data.name),
                              minWidth: "24px",
                            }}
                          >
                            {data.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                            <Flex align="center" gap={5} style={{ minWidth: 0 }}>
                              <Typography className={`${styles[`${componentName}__container-client-name`]}`}>
                                {data.name}
                              </Typography>
                              {!data.is_owned && data.owner_name && (
                                <Tooltip placement="top" title={`Shared by ${data.owner_name}`}>
                                  <svg
                                    style={{ flexShrink: 0 }}
                                    width="14"
                                    height="11"
                                    viewBox="0 0 14 11"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M7.41792 2.5C7.41792 3.16304 7.15453 3.79893 6.68569 4.26777C6.21685 4.73661 5.58096 5 4.91792 5C4.25488 5 3.61899 4.73661 3.15015 4.26777C2.68131 3.79893 2.41792 3.16304 2.41792 2.5C2.41792 1.83696 2.68131 1.20107 3.15015 0.732233C3.61899 0.263392 4.25488 0 4.91792 0C5.58096 0 6.21685 0.263392 6.68569 0.732233C7.15453 1.20107 7.41792 1.83696 7.41792 2.5ZM9.81792 10.006C9.92792 10.548 9.46992 11 8.91792 11H0.917919C0.364919 11 -0.0920809 10.548 0.0159191 10.006C0.245969 8.87575 0.859558 7.85974 1.75279 7.13C2.64602 6.40027 3.764 6.00165 4.91742 6.00165C6.07084 6.00165 7.18882 6.40027 8.08205 7.13C8.97528 7.85974 9.58887 8.87575 9.81892 10.006H9.81792ZM12.9199 10H11.3299C11.3221 9.90265 11.3088 9.80582 11.2899 9.71C11.0992 8.76638 10.7006 7.87714 10.1229 7.107C10.8579 6.9047 11.6424 6.98961 12.3171 7.34451C12.9918 7.69941 13.5062 8.29771 13.7559 9.018C13.9359 9.54 13.4729 10 12.9199 10ZM10.9179 6C11.4484 6 11.9571 5.78929 12.3321 5.41421C12.7072 5.03914 12.9179 4.53043 12.9179 4C12.9179 3.46957 12.7072 2.96086 12.3321 2.58579C11.9571 2.21071 11.4484 2 10.9179 2C10.3875 2 9.87878 2.21071 9.50371 2.58579C9.12863 2.96086 8.91792 3.46957 8.91792 4C8.91792 4.53043 9.12863 5.03914 9.50371 5.41421C9.87878 5.78929 10.3875 6 10.9179 6Z"
                                      fill="#9F9F9F"
                                    />
                                  </svg>
                                </Tooltip>
                              )}
                            </Flex>
                            {selectedClient && selectedClient.id === data.id && inlineDetails.length > 0 && (
                              <Typography className={`${styles[`${componentName}__container-client-details`]}`}>
                                {inlineDetails.join(" · ")}
                              </Typography>
                            )}
                          </Flex>
                        </Flex>
                        {selectedClient && selectedClient.id === data.id && (
                          <Flex gap={10}>
                            <CloseOutlined
                              style={{ color: "var(--indigo-700)", marginRight: 5 }}
                              onClick={clearSelection}
                            />
                            {items && items.length > 0 && (
                              <Dropdown
                                menu={{ items, onClick: handleMenu }}
                                placement="bottomLeft"
                                arrow
                                trigger={["click"]}
                              >
                                <MoreOutlined style={{ color: "var(--indigo-700)" }} />
                              </Dropdown>
                            )}
                          </Flex>
                        )}
                      </Flex>
                    );
                  })}
                </>
              )}
            </>
          ) : (
            <EmptyStateClient segment={selectedSegment} />
          )}
        </Flex>
      }
    </Flex>
  );
};

export default ClientView;
